import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { api, formatError } from "../../lib/api";
import { AdminTitle, AdminField, AdminCheck, Panel } from "./AdminUi";
import { MarketExceptions } from "./AdminMarketExceptions";
import { MarketEvents } from "./AdminMarketEvents";

export const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const EMPTY = { weekday: 0, city: "", name: "", start_time: "08:00", end_time: "13:00", location: "", maps_url: "", status: "confirme", note: "", active: true, internal_fee: "", internal_note: "" };
const ST = { confirme: "Confirmé", a_confirmer: "À confirmer" };
const sel = "mt-1.5 w-full bg-[#0B0B0B] border border-[#C9A45C]/25 px-3 py-2.5 text-sm";

const ScheduleForm = ({ f, setF, onSave }) => {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={onSave} className="grid sm:grid-cols-2 gap-4" data-testid="schedule-form">
      <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">Jour</span>
        <select value={f.weekday} onChange={(e) => setF({ ...f, weekday: Number(e.target.value) })} className={sel} data-testid="schedule-form-weekday">
          {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select></label>
      <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">Statut habituel</span>
        <select value={f.status} onChange={set("status")} className={sel} data-testid="schedule-form-status">
          {Object.entries(ST).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select></label>
      <AdminField label="Ville" required value={f.city} onChange={set("city")} testid="schedule-form-city" />
      <AdminField label="Nom du marché" value={f.name} onChange={set("name")} testid="schedule-form-name" />
      <AdminField label="Début" type="time" value={f.start_time} onChange={set("start_time")} testid="schedule-form-start" />
      <AdminField label="Fin" type="time" value={f.end_time} onChange={set("end_time")} testid="schedule-form-end" />
      <AdminField className="sm:col-span-2" label="Adresse ou emplacement" value={f.location} onChange={set("location")} testid="schedule-form-location" />
      <AdminField className="sm:col-span-2" label="Lien Google Maps (facultatif, sinon automatique)" value={f.maps_url} onChange={set("maps_url")} testid="schedule-form-maps" />
      <AdminField className="sm:col-span-2" label="Remarque visible par les clients" value={f.note} onChange={set("note")} testid="schedule-form-note" />
      <div className="sm:col-span-2 border border-dashed border-[#C9A45C]/30 p-4 grid sm:grid-cols-2 gap-4">
        <p className="sm:col-span-2 text-[10px] tracking-[0.2em] uppercase text-[#E5C178]">Gestion interne — jamais affiché sur le site</p>
        <AdminField label="Tarif d'emplacement (€)" type="number" min="0" step="0.01" value={f.internal_fee ?? ""} onChange={set("internal_fee")} testid="schedule-form-fee" />
        <AdminField label="Note interne" value={f.internal_note} onChange={set("internal_note")} testid="schedule-form-internal-note" />
      </div>
      <div className="sm:col-span-2"><AdminCheck label="Afficher ce marché dans le calendrier" checked={f.active} onChange={(v) => setF({ ...f, active: v })} testid="schedule-form-active" /></div>
      <div className="sm:col-span-2 flex justify-end gap-3">
        <button type="button" onClick={() => setF(null)} className="btn-outline-gold !py-3">Annuler</button>
        <button type="submit" className="btn-gold !py-3" data-testid="schedule-form-submit">Enregistrer</button>
      </div>
    </form>
  );
};

export default function AdminMarkets() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(null);
  const load = () => api.get("/admin/market-schedule").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, internal_fee: f.internal_fee === "" || f.internal_fee == null ? null : Number(f.internal_fee) };
    try {
      if (f.id) await api.put(`/admin/market-schedule/${f.id}`, body); else await api.post("/admin/market-schedule", body);
      toast.success("Planning enregistré"); setF(null); load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const del = async (m) => { if (window.confirm(`Supprimer le marché de ${m.city} du ${DAYS[m.weekday].toLowerCase()} ?`)) { await api.delete(`/admin/market-schedule/${m.id}`); load(); } };

  return (
    <div data-testid="admin-markets" className="space-y-14">
      <div>
        <AdminTitle title="Planning des marchés" action={<button className="btn-gold !py-3" onClick={() => setF({ ...EMPTY })} data-testid="admin-add-schedule-button"><Plus className="h-4 w-4" />Ajouter un marché</button>}>
          Votre semaine type, affichée chaque semaine sur la page « Nos marchés ».
        </AdminTitle>
        <div className="space-y-3">
          {items.map((m) => (
            <Panel key={m.id} className={`p-4 flex items-center gap-4 ${m.active ? "" : "opacity-50"}`} data-testid={`admin-schedule-${m.id}`}>
              <p className="eyebrow w-24 shrink-0">{DAYS[m.weekday]}</p>
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl truncate">{m.city}</p>
                <p className="text-xs text-[#F8F6F1]/50">{m.start_time}–{m.end_time} · {ST[m.status]}{m.internal_fee != null ? ` · emplacement ${m.internal_fee} € (interne)` : ""}{m.active ? "" : " · masqué"}</p>
              </div>
              <button onClick={() => setF({ ...EMPTY, ...m })} className="p-2 hover:text-[#C9A45C]" aria-label="Modifier" data-testid={`admin-edit-schedule-${m.id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(m)} className="p-2 hover:text-red-400" aria-label="Supprimer" data-testid={`admin-delete-schedule-${m.id}`}><Trash2 className="h-4 w-4" /></button>
            </Panel>
          ))}
          {!items.length && <p className="text-sm text-[#F8F6F1]/45">Aucun marché dans le planning.</p>}
        </div>
      </div>
      <MarketExceptions schedule={items} />
      <MarketEvents />
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="bg-[#101010] border-[#C9A45C]/30 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-display text-2xl font-normal">{f?.id ? "Modifier le marché" : "Nouveau marché"}</DialogTitle>
          {f && <ScheduleForm f={f} setF={setF} onSave={save} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
